import { z } from 'zod';

// AR collection-behavior engine (ALC-1418)
//
// Learns per-customer collection behavior from reconciled receipts and
// projects an expected cash date onto every open AR payment schedule.

export const ArCollectionSettingsSchema = z.object({
  /**
   * How many most-recent settled invoices feed a collection-behavior sample.
   * Applies to every aggregation scope (customer, billing group, tenant).
   */
  collectionBehaviorLookbackInvoices: z.number().int().min(1).max(24),
});

export type ArCollectionSettings = z.infer<typeof ArCollectionSettingsSchema>;

export const AR_COLLECTION_LOOKBACK_MIN = 1;
export const AR_COLLECTION_LOOKBACK_MAX = 24;
export const AR_COLLECTION_LOOKBACK_DEFAULT = 6;

/**
 * Minimum settled invoices a scope needs before its behavior is trusted.
 * A customer below this falls back to billing group, then tenant, flagged
 * low confidence.
 */
export const AR_COLLECTION_MIN_SETTLED_INVOICES = 3;

/** In-range integer lookbacks pass through; anything else gets the default. */
export function clampCollectionLookback(value: unknown): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= AR_COLLECTION_LOOKBACK_MIN &&
    value <= AR_COLLECTION_LOOKBACK_MAX
    ? value
    : AR_COLLECTION_LOOKBACK_DEFAULT;
}

/**
 * Days after its last installment's due date before an invoice counts as
 * "matured" for collection-rate purposes (ALC-1422): young receivables are
 * still inside normal payment lag and would read as uncollected. 60 days is a
 * judgment call, not a spec-given number — it comfortably exceeds the payment
 * lags the behavior engine observes while keeping the rate sample recent.
 */
export const AR_COLLECTION_MATURITY_DAYS = 60;

/** Which aggregation scope produced the expected cash date. */
export type CollectionBehaviorSource = 'CUSTOMER' | 'BILLING_GROUP' | 'TENANT' | 'NONE';

/** HIGH only when the customer's own history produced the estimate. */
export type CollectionConfidence = 'HIGH' | 'LOW';

export interface CollectionBehaviorEstimate {
  /** Median (receipt date − due date) in days over the sampled invoices. */
  medianLagDays: number;
  /** Settled invoices in the sample that produced the estimate. */
  sampleSize: number;
  source: CollectionBehaviorSource;
  confidence: CollectionConfidence;
}

/**
 * Collection rate learned from the same history the behavior engine reads
 * (ALC-1422): over the `lookback` most recently matured invoices of a scope,
 * collected amount ÷ billed amount, clamped to [0, 1]. Resolved through the
 * same customer → billing-group → tenant fallback chain as the lag estimate.
 * `NONE` means no matured history anywhere in the chain — the rate is then 1
 * (no haircut is invented without evidence).
 */
export interface CollectionRateEstimate {
  /** Collected ÷ billed over the sampled matured invoices, in [0, 1]. */
  collectionRate: number;
  /** Matured invoices in the sample that produced the rate. */
  sampleSize: number;
  source: CollectionBehaviorSource;
  confidence: CollectionConfidence;
}

/** Expected-cash annotation carried by an open AR payment schedule. */
export interface ArScheduleExpectedCash {
  /** ISO date (YYYY-MM-DD): schedule due date shifted by the learned lag. */
  expectedCashDate: string;
  confidence: CollectionConfidence;
  source: CollectionBehaviorSource;
  medianLagDays: number;
  sampleSize: number;
}
